import React, { useEffect, useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import api from "@/lib/api";
import { Plus, Trash2 } from "lucide-react";

const Section = ({ title, subtitle, children }) => (
  <div className="bg-white border border-[#e6e2d6] rounded-2xl p-6 md:p-8">
    <div className="label-eyebrow mb-1">{title}</div>
    {subtitle && (
      <p className="text-sm text-stone-500 mb-6">{subtitle}</p>
    )}
    {!subtitle && <div className="mb-6" />}
    {children}
  </div>
);

const Field = ({ label, children, testid }) => (
  <div>
    <Label className="text-xs font-semibold text-stone-600">{label}</Label>
    <div className="mt-1" data-testid={testid ? `field-${testid}` : undefined}>
      {children}
    </div>
  </div>
);

const stoneInput = "bg-stone-50 border-stone-200";

export default function FamilyFormFields({ value, onChange, adminMode = false }) {
  const set = (patch) => onChange({ ...value, ...patch });
  const [familiesList, setFamiliesList] = useState([]);

  useEffect(() => {
    if (adminMode) {
      api
        .get("/families")
        .then((r) => setFamiliesList(r.data || []))
        .catch(() => setFamiliesList([]));
    }
  }, [adminMode]);

  const parentOptions = familiesList
    .filter((f) => f._id !== value._id)
    .map((f) => ({
      id: f._id,
      label: `${f.first_name || ""} ${f.last_name || ""}`.trim() || "Unnamed",
    }));

  const setChild = (i, patch) => {
    const next = [...(value.children || [])];
    next[i] = { ...next[i], ...patch };
    set({ children: next });
  };
  const addChild = () =>
    set({
      children: [
        ...(value.children || []),
        { first_name: "", last_name: "", birth_date: "", _key: `new-${Date.now()}-${Math.random().toString(36).slice(2, 8)}` },
      ],
    });
  const removeChild = (i) => {
    const next = [...(value.children || [])];
    next.splice(i, 1);
    set({ children: next });
  };

  // Stable keys for children: prefer the _key added on client, fall back to a
  // once-assigned position marker so existing db-loaded rows don't remount.
  const childKeys = useMemo(
    () =>
      (value.children || []).map(
        (c, i) => c?._key || `existing-${i}-${c?.first_name || ""}-${c?.birth_date || ""}`
      ),
    [value.children]
  );

  return (
    <div className="space-y-6">
      <Section title="About you" subtitle="Basic contact information.">
        <div className="grid md:grid-cols-2 gap-5">
          <Field label="First name" testid="first-name">
            <Input
              value={value.first_name || ""}
              onChange={(e) => set({ first_name: e.target.value })}
              className={stoneInput}
              data-testid="input-first-name"
            />
          </Field>
          <Field label="Last name" testid="last-name">
            <Input
              value={value.last_name || ""}
              onChange={(e) => set({ last_name: e.target.value })}
              className={stoneInput}
              data-testid="input-last-name"
            />
          </Field>
          <Field label="Email">
            <Input
              type="email"
              value={value.email || ""}
              onChange={(e) => set({ email: e.target.value })}
              className={stoneInput}
              data-testid="input-email"
            />
          </Field>
          <Field label="Phone">
            <Input
              value={value.phone || ""}
              onChange={(e) => set({ phone: e.target.value })}
              className={stoneInput}
              data-testid="input-phone"
            />
          </Field>
          <Field label="Birth date">
            <Input
              type="date"
              value={value.birth_date || ""}
              onChange={(e) => set({ birth_date: e.target.value })}
              className={stoneInput}
              data-testid="input-birth-date"
            />
          </Field>
        </div>
      </Section>

      <Section
        title="Spouse"
        subtitle="If a spouse or partner shares this household."
      >
        <div className="grid md:grid-cols-2 gap-5">
          <Field label="Spouse's first name" testid="partner-first-name">
            <Input
              value={value.partner_first_name || ""}
              onChange={(e) => set({ partner_first_name: e.target.value })}
              className={stoneInput}
              data-testid="input-partner-first-name"
            />
          </Field>
          <Field label="Spouse's last name (if different)" testid="partner-last-name">
            <Input
              value={value.partner_last_name || ""}
              onChange={(e) => set({ partner_last_name: e.target.value })}
              placeholder={value.last_name || ""}
              className={stoneInput}
              data-testid="input-partner-last-name"
            />
          </Field>
          <Field label="Spouse's birth date">
            <Input
              type="date"
              value={value.partner_birth_date || ""}
              onChange={(e) => set({ partner_birth_date: e.target.value })}
              className={stoneInput}
              data-testid="input-partner-birth-date"
            />
          </Field>
          <Field label="Spouse's phone" testid="partner-phone">
            <Input
              value={value.partner_phone || ""}
              onChange={(e) => set({ partner_phone: e.target.value })}
              placeholder="Optional"
              className={stoneInput}
              data-testid="input-partner-phone"
            />
          </Field>
          <Field label="Spouse's email" testid="partner-email">
            <Input
              type="email"
              value={value.partner_email || ""}
              onChange={(e) => set({ partner_email: e.target.value })}
              placeholder="Optional"
              className={stoneInput}
              data-testid="input-partner-email"
            />
          </Field>
        </div>
      </Section>

      <Section
        title="Other adult"
        subtitle="Another grown-up in the household — a grandparent, adult child, or family member."
      >
        <div className="grid md:grid-cols-2 gap-5">
          <Field label="Their first name" testid="other-adult-first-name">
            <Input
              value={value.other_adult_first_name || ""}
              onChange={(e) => set({ other_adult_first_name: e.target.value })}
              className={stoneInput}
              data-testid="input-other-adult-first-name"
            />
          </Field>
          <Field label="Their last name (if different)" testid="other-adult-last-name">
            <Input
              value={value.other_adult_last_name || ""}
              onChange={(e) => set({ other_adult_last_name: e.target.value })}
              placeholder={value.last_name || ""}
              className={stoneInput}
              data-testid="input-other-adult-last-name"
            />
          </Field>
          <Field label="Their birth date">
            <Input
              type="date"
              value={value.other_adult_birth_date || ""}
              onChange={(e) => set({ other_adult_birth_date: e.target.value })}
              className={stoneInput}
              data-testid="input-other-adult-birth-date"
            />
          </Field>
        </div>
      </Section>

      <Section title="Mailing address" subtitle="Used for reunion invitations & labels.">
        <div className="grid md:grid-cols-2 gap-5">
          <div className="md:col-span-2">
            <Field label="Street address">
              <Input
                value={value.address_line1 || ""}
                onChange={(e) => set({ address_line1: e.target.value })}
                className={stoneInput}
                data-testid="input-address1"
              />
            </Field>
          </div>
          <div className="md:col-span-2">
            <Field label="Apartment, unit, etc. (optional)">
              <Input
                value={value.address_line2 || ""}
                onChange={(e) => set({ address_line2: e.target.value })}
                className={stoneInput}
                data-testid="input-address2"
              />
            </Field>
          </div>
          <Field label="City">
            <Input
              value={value.city || ""}
              onChange={(e) => set({ city: e.target.value })}
              className={stoneInput}
              data-testid="input-city"
            />
          </Field>
          <Field label="State / Region">
            <Input
              value={value.state || ""}
              onChange={(e) => set({ state: e.target.value })}
              className={stoneInput}
              data-testid="input-state"
            />
          </Field>
          <Field label="Zip / Postal code">
            <Input
              value={value.zip_code || ""}
              onChange={(e) => set({ zip_code: e.target.value })}
              className={stoneInput}
              data-testid="input-zip"
            />
          </Field>
        </div>
      </Section>

      <Section title="Your parents" subtitle="For the family tree.">
        <div className="grid md:grid-cols-2 gap-5">
          <Field label="Father's name">
            <Input
              value={value.father_name || ""}
              onChange={(e) => set({ father_name: e.target.value })}
              className={stoneInput}
              data-testid="input-father"
            />
          </Field>
          <Field label="Mother's name">
            <Input
              value={value.mother_name || ""}
              onChange={(e) => set({ mother_name: e.target.value })}
              className={stoneInput}
              data-testid="input-mother"
            />
          </Field>
          {adminMode && (
            <>
              <Field label="Link father to family record (optional)">
                <Select
                  value={value.father_family_id || "__none__"}
                  onValueChange={(v) =>
                    set({ father_family_id: v === "__none__" ? null : v })
                  }
                >
                  <SelectTrigger className={stoneInput} data-testid="select-father-family">
                    <SelectValue placeholder="Not linked" />
                  </SelectTrigger>
                  <SelectContent className="bg-white max-h-72">
                    <SelectItem value="__none__">Not linked</SelectItem>
                    {parentOptions.map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Link mother to family record (optional)">
                <Select
                  value={value.mother_family_id || "__none__"}
                  onValueChange={(v) =>
                    set({ mother_family_id: v === "__none__" ? null : v })
                  }
                >
                  <SelectTrigger className={stoneInput} data-testid="select-mother-family">
                    <SelectValue placeholder="Not linked" />
                  </SelectTrigger>
                  <SelectContent className="bg-white max-h-72">
                    <SelectItem value="__none__">Not linked</SelectItem>
                    {parentOptions.map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
            </>
          )}
        </div>
      </Section>

      <Section title="Children in household" subtitle="Add one row per child living with you.">
        <div className="space-y-3">
          {(value.children || []).map((c, i) => (
            <div
              key={childKeys[i]}
              className="grid grid-cols-1 md:grid-cols-[1fr_1fr_150px_auto] gap-3 items-end bg-[#faf7ef] border border-[#e6e2d6] rounded-xl p-3"
              data-testid={`child-row-${i}`}
            >
              <Field label="First name">
                <Input
                  type="text"
                  inputMode="text"
                  autoComplete="off"
                  value={c.first_name || ""}
                  onChange={(e) => setChild(i, { first_name: e.target.value })}
                  className="bg-white border-stone-200"
                  data-testid={`input-child-name-${i}`}
                />
              </Field>
              <Field label="Last name">
                <Input
                  type="text"
                  inputMode="text"
                  autoComplete="off"
                  value={c.last_name || ""}
                  onChange={(e) => setChild(i, { last_name: e.target.value })}
                  className="bg-white border-stone-200"
                  data-testid={`input-child-last-${i}`}
                />
              </Field>
              <Field label="Birth date">
                <Input
                  type="date"
                  value={c.birth_date || ""}
                  onChange={(e) => setChild(i, { birth_date: e.target.value })}
                  className="bg-white border-stone-200"
                  data-testid={`input-child-birth-${i}`}
                />
              </Field>
              <Button
                type="button"
                variant="ghost"
                onClick={() => removeChild(i)}
                className="text-stone-500 hover:text-red-600 hover:bg-red-50 h-10"
                data-testid={`remove-child-${i}`}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}
          <Button
            type="button"
            onClick={addChild}
            variant="outline"
            className="rounded-full border-[#1a2947] text-[#1a2947] hover:bg-[#1a2947]/5 btn-pill"
            data-testid="add-child-btn"
          >
            <Plus className="h-4 w-4 mr-1" /> Add child
          </Button>
        </div>
      </Section>

      <Section title="Notes" subtitle="Dietary needs, wheelchair access, anything.">
        <Textarea
          value={value.notes || ""}
          onChange={(e) => set({ notes: e.target.value })}
          rows={4}
          className={stoneInput}
          data-testid="input-notes"
        />
      </Section>
    </div>
  );
}
